// src/components/dashboard/EventDetails/EventCard.jsx
// 🔧 添加可控制显示/隐藏字段的EventCard组件

import React from 'react';
import Card from '../../ui/Card/Card';
import Badge from '../../ui/Badge/Badge';
import { formatDateTime, formatRelativeTime, getSeverityVariant, getSeverityIcon, formatSeverity } from '../../../utils/dateFormatter';
import styles from './EventDetails.module.css';

const EventCard = ({ 
  event, 
  sensor, 
  isSelected = false, 
  onClick,
  size = 'md', // sm, md, lg
  // 🔧 新增：控制显示/隐藏的选项
  showFields = {
    description: true,
    timestamp: true,
    sensorId: true,
    severity: true,
    location: true,
    borough: true,
    waterDepth: true
  }
}) => {
  if (!event || !sensor) {
    return null;
  }

  const handleClick = () => {
    if (onClick) {
      onClick(sensor);
    }
  };

  return (
    <Card 
      variant={isSelected ? "glass" : "default"}
      size="sm"
      interactive
      hover
      onClick={handleClick}
      className={`${styles.eventCard} ${isSelected ? styles.selected : ''}`}
    >
      <Card.Body padding={size !== 'sm'}>
        <div className={styles.eventCardContent}>

          {/* Header 部分（地点 + Badge） */}
          {(showFields.location || showFields.waterDepth) && (
            <div className={styles.eventCardHeader}>
              {showFields.location && (
                <div className={styles.eventLocation}>
                  <h4 className={styles.locationName}>{sensor.location}</h4>
                  {showFields.borough && (
                    <p className={styles.locationBorough}>{sensor.borough}</p>
                  )}
                </div>
              )}

              {showFields.waterDepth && (
                <Badge
                  variant={getSeverityVariant(event.severity)}
                  size={size === 'lg' ? 'md' : (size === 'sm' ? 'xs' : 'sm')}
                  icon={getSeverityIcon(event.severity)}
                  pulse={event.severity === 'severe'}
                >
                  {event.water_depth_inches}"
                </Badge>
              )}
            </div>
          )}

          {/* 描述 */}
          {showFields.description && (size === 'md' || size === 'lg') && (
            <p className={styles.eventDescription}>
              {event.description}
            </p>
          )}

          {/* 水深文字 */}
          {showFields.waterDepth && size === 'lg' && (
            <div style={{ fontSize: '0.875rem', color: '#2563eb' }}>
              Water Depth: {event.water_depth_inches} inches
            </div>
          )}

          {/* Footer */}
          <div className={styles.eventCardFooter}>
            <div className={styles.eventMeta}>
              {showFields.severity && (
                <span className={styles.severityText}>
                  {formatSeverity(event.severity)} Flooding
                </span>
              )}
              {showFields.timestamp && (
                <span className={styles.timestamp}>
                  {size === 'sm' ? formatRelativeTime(event.timestamp) : formatDateTime(event.timestamp)}
                </span>
              )}
            </div>

            {showFields.sensorId && sensor.id && (
              <span className={styles.sensorId}>
                Sensor {sensor.id.slice(-6)}
              </span>
            )}
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

// 🔧 简化版本的EventCard - 专为Recent Events设计
export const EventCardCompact = ({ 
  event, 
  sensor, 
  isSelected, 
  onClick,
  showMinimal = false  // 🔑 新增：极简模式
}) => {
  if (showMinimal) {
    // 🔧 极简模式：只显示关键信息
    return (
      <EventCard 
        event={event}
        sensor={sensor}
        isSelected={isSelected}
        onClick={onClick}
        size="sm"
        showFields={{
          description: false,     // 隐藏描述
          timestamp: true,        // 显示时间
          sensorId: false,        // 隐藏传感器ID
          severity: false,        // 隐藏严重程度文字
          location: true,         // 显示位置
          borough: false,         // 隐藏区域
          waterDepth: true        // 显示水深badge
        }}
      />
    );
  }

  // 🔧 紧凑模式：显示部分信息
  return (
    <EventCard 
      event={event}
      sensor={sensor}
      isSelected={isSelected}
      onClick={onClick}
      size="md"
      showFields={{
        description: false,     // 🔧 隐藏描述（太长）
        timestamp: true,        // 显示时间
        sensorId: false,        // 🔧 隐藏传感器ID（减少混乱）
        severity: true,         // 显示严重程度
        location: true,         // 显示位置
        borough: true,          // 显示区域
        waterDepth: true        // 显示水深
      }}
    />
  );
};

// 🔧 超级简化版本
export const EventCardMinimal = ({ event, sensor, isSelected, onClick }) => {
  const handleClick = () => {
    if (onClick) onClick(sensor);
  };

  return (
    <div 
      onClick={handleClick}
      className={`p-2 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
        isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-gray-900 truncate">
            {sensor.location}
          </div>
          <div className="text-xs text-gray-500">
            {formatRelativeTime(event.timestamp)}
          </div>
        </div>
        <div className="ml-2 flex items-center space-x-2">
          <span className="text-sm font-bold text-blue-600">
            {event.water_depth_inches}"
          </span>
          <div className={`w-2 h-2 rounded-full ${
            event.severity === 'severe' ? 'bg-red-500' :
            event.severity === 'moderate' ? 'bg-orange-500' : 'bg-yellow-500'
          }`}></div>
        </div>
      </div>
    </div>
  );
};

export default EventCard;